import { useCallback, useEffect, useState } from "react";
import { Play, Square, Trash2, Save } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { useTerminal } from "@/lib/terminal";
import { errMsg } from "@/lib/api";
import { EXPLORER } from "@/lib/format";

const Field = ({ label, hint, children }) => (
  <label className="block space-y-1">
    <span className="text-xs text-slate-400">{label}</span>
    {children}
    {hint && <span className="block text-[10px] text-slate-600">{hint}</span>}
  </label>
);

const NumInput = (props) => (
  <input type="number" {...props} className="w-full h-9 px-2.5 rounded border border-[#222634] bg-[#161920] font-mono text-sm outline-none focus:border-[#333A4E]" />
);

const LEVEL = { success: "text-[#00E676]", error: "text-[#FF5252]", warn: "text-[#FFD700]", info: "text-slate-300" };

const linkify = (msg) => msg.split(/(0x[a-fA-F0-9]{64})/).map((part, i) =>
  /^0x[a-fA-F0-9]{64}$/.test(part) ? <a key={i} href={`${EXPLORER}/tx/${part}`} target="_blank" rel="noreferrer" className="underline">{part.slice(0, 10)}…</a> : part);

export default function BotPage() {
  const { api, wallet } = useTerminal();
  const [st, setSt] = useState(null);
  const [form, setForm] = useState(null);

  const load = useCallback(async () => {
    const d = await api("get", "/bot");
    setSt(d);
    setForm((f) => f || d.settings);
  }, [api]);

  useEffect(() => { load().catch(() => {}); const t = setInterval(() => load().catch(() => {}), 3000); return () => clearInterval(t); }, [load]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const payload = () => ({
    amount_eth: Number(form.amount_eth), slippage_bps: Math.min(10000, Math.max(0, Math.round(Number(form.slippage_bps) || 0))), daily_limit_eth: Number(form.daily_limit_eth),
    tp_enabled: !!form.tp_enabled, tp_pct: Number(form.tp_pct) || 100, sl_enabled: !!form.sl_enabled, sl_pct: Math.min(100, Number(form.sl_pct) || 50),
    max_tax_enabled: !!form.max_tax_enabled, max_tax_pct: Math.min(100, Number(form.max_tax_pct) || 4),
  });
  const anyPrice = Number(form?.slippage_bps) >= 10000;
  const save = async () => {
    try { await api("put", "/bot/settings", payload()); toast.success("Bot ayarları kaydedildi"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const toggle = async () => {
    try {
      if (st.alive) { await api("post", "/bot/stop"); toast("Sniper bot durduruldu"); }
      else { await api("put", "/bot/settings", payload()); await api("post", "/bot/start"); toast.success("Sniper bot başlatıldı"); }
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  if (!st || !form) return <p className="p-6 text-slate-500">Yükleniyor…</p>;
  const limitPct = Math.min(100, (st.spent_today / (st.settings.daily_limit_eth || 1)) * 100);
  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-3">
      <div className="lg:col-span-4 space-y-3">
        <div className={`p-4 rounded-md border ${st.alive ? "border-[#00E676]/40 bg-[#00E676]/5" : "border-[#222634] bg-[#101216]"}`}>
          <div className="flex items-center gap-2">
            <span className={`w-2 h-2 rounded-full ${st.alive ? "bg-[#00E676] live-dot" : "bg-slate-600"}`} />
            <h1 className="text-xl font-bold tracking-tight">Sniper Bot</h1>
            <span className="ml-auto text-xs font-mono text-slate-500" data-testid="bot-last-pool">#{st.last_pool || "-"}</span>
          </div>
          <p className={`mt-1 text-[11px] font-mono ${st.private_rpc ? "text-[#00E676]" : "text-[#FFD700]"}`} data-testid="bot-rpc-status">RPC: {st.private_rpc ? "özel (hızlı)" : "herkese açık (429 limiti olabilir)"}</p>
          <p className="mt-2 text-sm text-slate-400">Başlattığında full.fun'da basılan her yeni coinden belirlediğin miktarda otomatik alır. Zincir her 1.5 saniyede taranır.</p>
          <button data-testid="bot-start-stop-toggle" onClick={toggle} disabled={!wallet?.address}
            className={`mt-4 w-full h-12 rounded font-bold flex items-center justify-center gap-2 transition-colors disabled:opacity-40 ${st.alive ? "bg-[#FF5252] hover:bg-[#D50000] text-[#08090C]" : "bg-[#00E676] hover:bg-[#00C853] text-[#08090C]"}`}>
            {st.alive ? <><Square className="w-4 h-4" /> Botu Durdur</> : <><Play className="w-4 h-4" /> Botu Başlat</>}
          </button>
          {!wallet?.address && <p className="mt-2 text-xs text-[#FFD700]">Botu başlatmak için önce Cüzdan sayfasından private key ekle.</p>}
          <div className="mt-4">
            <div className="flex justify-between text-xs font-mono text-slate-400"><span>Bugün harcanan</span><span data-testid="bot-spent-today">{st.spent_today.toFixed(4)} / {st.settings.daily_limit_eth} Ξ</span></div>
            <div className="mt-1 h-1.5 rounded bg-[#161920] overflow-hidden"><div className="h-full bg-[#FFD700]" style={{ width: `${limitPct}%` }} /></div>
          </div>
        </div>
        <div className="p-4 rounded-md border border-[#222634] bg-[#101216] space-y-3">
          <h2 className="text-sm font-semibold">Ayarlar</h2>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Coin başı alım (ETH)"><NumInput data-testid="bot-eth-amount-input" step="0.001" value={form.amount_eth} onChange={set("amount_eth")} /></Field>
            <Field label="Günlük limit (ETH)"><NumInput data-testid="bot-daily-limit-input" step="0.01" value={form.daily_limit_eth} onChange={set("daily_limit_eth")} /></Field>
            <Field label="Kayma (bps)" hint="2000 = %20 · 10000 = sınırsız"><NumInput data-testid="bot-slippage-input" step="100" max="10000" value={form.slippage_bps} onChange={set("slippage_bps")} disabled={anyPrice} /></Field>
            <div className={`p-2.5 rounded border ${anyPrice ? "border-[#FFD700]/40 bg-[#FFD700]/5" : "border-[#222634]"}`}>
              <span className="flex items-center gap-2 text-xs text-slate-300">Her fiyattan al
                <Switch data-testid="bot-any-price-switch" checked={anyPrice} onCheckedChange={(v) => setForm({ ...form, slippage_bps: v ? 10000 : 2000 })} />
              </span>
              <span className="block mt-1 text-[10px] text-slate-500">Kayma sınırı yok: coin hangi seviyede olursa olsun alım yapılır.</span>
            </div>
          </div>
          <div className="pt-2 border-t border-[#222634]">
            <Field label={<span className="flex items-center gap-2">Vergi filtresi<Switch data-testid="bot-max-tax-switch" checked={!!form.max_tax_enabled} onCheckedChange={(v) => setForm({ ...form, max_tax_enabled: v })} /></span>}
              hint="Alım veya satım vergisi bu değer ve üstündeyse bot o coini almaz">
              <div className="flex items-center gap-2">
                <NumInput data-testid="bot-max-tax-input" step="0.5" min="0" value={form.max_tax_pct} onChange={set("max_tax_pct")} disabled={!form.max_tax_enabled} />
                <span className="text-xs text-slate-500 whitespace-nowrap">% ve üstü alma</span>
              </div>
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3 pt-2 border-t border-[#222634]">
            <Field label={<span className="flex items-center gap-2">Kâr Al %<Switch data-testid="bot-tp-switch" checked={!!form.tp_enabled} onCheckedChange={(v) => setForm({ ...form, tp_enabled: v })} /></span>}>
              <NumInput data-testid="bot-take-profit-input" value={form.tp_pct} onChange={set("tp_pct")} disabled={!form.tp_enabled} />
            </Field>
            <Field label={<span className="flex items-center gap-2">Zarar Durdur %<Switch data-testid="bot-sl-switch" checked={!!form.sl_enabled} onCheckedChange={(v) => setForm({ ...form, sl_enabled: v })} /></span>}>
              <NumInput data-testid="bot-stop-loss-input" value={form.sl_pct} onChange={set("sl_pct")} disabled={!form.sl_enabled} />
            </Field>
          </div>
          <p className="text-[11px] text-slate-500">TP/SL bot tarafından alınan pozisyonlara uygulanır; hedef görüldüğünde %100 satılır.</p>
          <button data-testid="bot-save-settings" onClick={save} className="w-full h-9 rounded border border-[#333A4E] hover:bg-[#161920] text-sm flex items-center justify-center gap-2 transition-colors"><Save className="w-4 h-4" /> Ayarları Kaydet</button>
        </div>
      </div>
      <div className="lg:col-span-8 rounded-md border border-[#222634] bg-[#0A0C10] flex flex-col min-h-[500px]" data-testid="bot-log">
        <div className="flex items-center px-3 h-10 border-b border-[#222634]">
          <h2 className="text-sm font-semibold">Canlı Bot Logu</h2>
          <button data-testid="bot-clear-logs" onClick={async () => { await api("delete", "/bot/logs"); load(); }} className="ml-auto text-slate-500 hover:text-slate-200"><Trash2 className="w-4 h-4" /></button>
        </div>
        <div className="flex-1 overflow-y-auto terminal-scroll p-3 font-mono text-xs space-y-1 max-h-[calc(100vh-10rem)]">
          {st.logs.length === 0 && <p className="text-slate-600">Henüz log yok. Botu başlatınca burada canlı akış göreceksin.</p>}
          {st.logs.map((l) => (
            <div key={l.id} className="flex gap-3">
              <span className="text-slate-600 shrink-0">{new Date(l.ts).toLocaleTimeString("tr-TR")}</span>
              <span className={`${LEVEL[l.level]} break-all`}>{linkify(l.msg)}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
